import { BadgeCheck, Hash, Map as MapIcon, Receipt } from 'lucide-react';
import {
  DataTable,
  DocNote,
  FilterBar,
  MetricCard,
  MetricRow,
  OpenFlag,
  SelectableChip,
  StatusPill,
  type DataTableColumn,
  RangePicker,
} from '../../components';
import { formatDate } from '../../lib/format/dates';
import { formatCents, formatCount } from '../../lib/format/money';
import { useTableUrlState } from '../../lib/table/tableSearch';
import { pickParam } from '../profiles/people';
import type { NexusRow } from './api';
import { useNexus } from './queries';
import { rangeTitle, useAnalyticsWindow } from '../analytics/parts';

const VIEWS = ['all', 'registered', 'unregistered'] as const;
const VIEW_LABEL = { all: 'All', registered: 'Registered', unregistered: 'Not registered' };

const COLUMNS: DataTableColumn<NexusRow>[] = [
  {
    id: 'state',
    header: 'State',
    width: '100px',
    cell: (r) => <span className="font-mono font-bold text-brown">{r.stateProvince}</span>,
  },
  {
    id: 'registered',
    header: 'Registered',
    width: '150px',
    cell: (r) => (
      <span>
        <StatusPill tone={r.registered ? 'success' : 'neutral'} dot>
          {r.registered ? 'Yes' : 'No'}
        </StatusPill>
        {r.registeredSince && (
          <span className="mt-0.5 block text-[11px] text-muted">since {formatDate(r.registeredSince, 'UTC')}</span>
        )}
      </span>
    ),
  },
  {
    id: 'sales',
    header: 'Sales, last 12 months',
    width: '170px',
    align: 'right',
    numeric: true,
    cell: (r) => formatCents(r.rolling12moGrossCents, { whole: true }),
  },
  {
    id: 'txns',
    header: 'Transactions',
    width: '130px',
    align: 'right',
    numeric: true,
    cell: (r) => formatCount(r.rolling12moTxnCount),
  },
  {
    id: 'threshold',
    header: 'Threshold',
    width: '1fr',
    cell: (r) =>
      r.thresholdSalesCents === undefined ? (
        <OpenFlag />
      ) : (
        <span className="text-[13px]">
          {formatCents(r.thresholdSalesCents, { whole: true })}
          {r.thresholdTxns ? <span className="text-muted"> or {formatCount(r.thresholdTxns)} txns</span> : null}
        </span>
      ),
  },
  {
    id: 'consumed',
    header: '% consumed',
    width: '1fr',
    cell: (r) =>
      r.pctConsumed === undefined ? (
        <span className="text-muted">—</span>
      ) : (
        <span className="inline-flex w-full items-center gap-2">
          <span className="h-2 flex-1 overflow-hidden rounded-full bg-[rgba(143,68,32,.1)]">
            <span
              className={`block h-full rounded-full ${r.pctConsumed >= 100 ? 'bg-danger' : r.pctConsumed >= 80 ? 'bg-olive' : 'bg-green'}`}
              style={{ width: `${Math.min(r.pctConsumed, 100)}%` }}
            />
          </span>
          <span className="tabular-nums">{r.pctConsumed.toFixed(0)}%</span>
        </span>
      ),
  },
  {
    id: 'projected',
    header: 'Projected crossing',
    width: '170px',
    cell: (r) => (
      <span className="text-muted">{r.projectedCrossing ? formatDate(r.projectedCrossing, 'UTC') : '—'}</span>
    ),
  },
];

/**
 * Nexus & thresholds (kit `NexusScreen`; §7.10). Economic-nexus exposure per
 * state over the trailing 12 months. A state with no threshold on file shows
 * OPEN — never a placeholder number.
 */
export function NexusScreen() {
  const table = useTableUrlState();
  const view = pickParam(table.filters.view, VIEWS) ?? 'all';
  // The activity cards follow the range (Last 12 months by default, UTC days); the
  // table is always rolling 12 months, the window nexus thresholds are tested on.
  const w = useAnalyticsWindow({ range: 'last-12mo', utc: true });
  const q = useNexus({ start: w.window.startDate, end: w.window.endDate });
  const all = q.data?.rows ?? [];
  const win = q.data?.window;
  const rangeLabel = rangeTitle(w.range).toLowerCase();
  const rows = all.filter((r) => view === 'all' || (view === 'registered') === r.registered);
  const n = (v: number | string) => (q.data ? v : q.isError ? '—' : '…');

  return (
    <div className="flex flex-col gap-[18px]">
      <div className="flex justify-end">
        <RangePicker value={w.range} onChange={w.setRange} />
      </div>
      <MetricRow>
        <MetricCard
          label="States with activity"
          value={n(win?.statesWithActivity ?? 0)}
          hint={rangeLabel}
          accent="var(--admin-chart-2)"
          icon={<MapIcon size={15} aria-hidden="true" />}
        />
        <MetricCard
          label="Registered"
          value={n(all.filter((r) => r.registered).length)}
          accent="var(--admin-chart-3)"
          icon={<BadgeCheck size={15} aria-hidden="true" />}
        />
        <MetricCard
          label="Facilitated sales"
          value={n(formatCents(win?.grossCents ?? 0, { whole: true }))}
          hint={`chef gross, not revenue · ${rangeLabel}`}
          accent="var(--admin-chart-1)"
          icon={<Receipt size={15} aria-hidden="true" />}
        />
        <MetricCard
          label="Transactions"
          value={n(formatCount(win?.txnCount ?? 0))}
          hint={rangeLabel}
          accent="var(--admin-chart-5)"
          icon={<Hash size={15} aria-hidden="true" />}
        />
      </MetricRow>

      <div>
        <FilterBar>
          {VIEWS.map((v) => (
            <SelectableChip
              key={v}
              active={view === v}
              onClick={() => table.setFilter('view', v === 'all' ? undefined : v)}
            >
              {VIEW_LABEL[v]}
            </SelectableChip>
          ))}
        </FilterBar>
        {view === 'unregistered' && (
          <DocNote>
            States where we sell or run a live locale but hold no state registration. A state with a live locale shows
            here even before its first sale.
          </DocNote>
        )}
        <DataTable
          label="Nexus by state"
          minWidth={1100}
          columns={COLUMNS}
          rows={rows}
          getRowId={(r) => r.stateProvince}
          renderSubline={(r) =>
            r.activeLocales?.length ? (
              <>
                <span className="font-semibold">Locales:</span> {r.activeLocales.join(' · ')}
              </>
            ) : null
          }
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          emptyLabel="No states match that filter."
        />
      </div>
    </div>
  );
}
